/* eslint-disable */
import { useEffect, useState } from 'react';
import _ from 'lodash';
import { t } from '@lingui/macro';
import { useFormContext } from 'react-hook-form';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import NgpField from './NgpField';

const addTagPlaceholder = t`Add Tag`;

const useStyles = makeStyles((theme) => ({
    autoComplete: {
        width: '95%',
        marginTop: theme.spacing(2),
    },
}));

function makeUnique(listOfTags = []) {
    return _.uniq(_.flatten(listOfTags));
}

/**
 * @typedef NgpTagsFieldProps
 * @property {string[]} [options] Pre-set tags to show
 */

/**
 *
 * @param {React.PropsWithoutRef<NgpTagsFieldProps>} fieldProps
 * @returns
 */
export function NgpTagsField(fieldProps) {
    const classes = useStyles();
    const { setValue, errors, register, watch } = useFormContext();
    const { fieldName, options = [], onChangeFunc = _.noop, allowNewTags = true } = fieldProps;
    const tagsValue = watch(fieldName);
    const [textFieldValue, setTextFieldValue] = useState();

    useEffect(() => {
        register({ name: fieldName });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [register]);

    const updateTags = () => {
        if (_.isEmpty(textFieldValue)) {
            return;
        }

        setValue(fieldName, makeUnique([...tagsValue, [textFieldValue]]), { shouldDirty: true });
        setTextFieldValue('');
    };

    const handleTagsChange = (e, fullTags) => {
        setValue(fieldName, makeUnique(fullTags), { shouldDirty: true });
        setTextFieldValue('');
        onChangeFunc(fullTags);
    };

    const handleTagsBlur = () => {
        if (allowNewTags) updateTags();
    };

    const handleTextKeyDown = (e) => {
        const tabKey = 'Tab';
        const enterKey = 'Enter';
        const { key } = e;

        if (!allowNewTags && (key === tabKey || key === enterKey)) {
            e.preventDefault();
            return;
        }

        if (key === tabKey && !_.isEmpty(textFieldValue)) {
            updateTags();
            e.target.focus();
            e.preventDefault();
        }
    };

    // Limit the length of an individual tag to 256 characters to prevent certain api calls from failing with long lists of non-ascii tags
    const updateTextField = (e) => {
        const tag = e.target.value;
        const tagLen = tag?.length || 0;
        const truncTag = tagLen > 256 ? tag.slice(0, 256) : tag;
        setTextFieldValue(truncTag);
        onChangeFunc(truncTag);
    };

    // Wait for `tagsValue` to be defined before rendering.  This ensures the `value` property is correctly bound to the
    // value on initial render and is necessary because `tagsValue` is initially `undefined`.
    if (!tagsValue) return null;

    return (
        <NgpField errors={errors} fieldName={fieldName}>
            <div className={classes.autoComplete}>
                <Autocomplete
                    className={classes.tagField}
                    freeSolo={allowNewTags}
                    multiple
                    autoHighlight
                    filterSelectedOptions
                    options={options}
                    value={tagsValue}
                    onChange={handleTagsChange}
                    renderInput={(params) => {
                        return (
                            <TextField
                                {...params}
                                placeholder={addTagPlaceholder}
                                value={textFieldValue}
                                onChange={updateTextField}
                                onBlur={handleTagsBlur}
                                onKeyDown={handleTextKeyDown}
                            />
                        );
                    }}
                />
            </div>
        </NgpField>
    );
}
